import { ChevronDown, Check } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './ui';
import s from './Select.module.css';

export type SelectOption = {
  value: string;
  label: string;
  /** Secondary text on the right of the row — a rate, a count, a badge. */
  hint?: string;
  disabled?: boolean;
};

type SelectProps = {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Renders the trigger and rows in the mono face — for tickers, model ids. */
  mono?: boolean;
  ariaLabel?: string;
  className?: string;
};

const POPUP_MARGIN = 6;
const MAX_POPUP_HEIGHT = 320;

/**
 * A select styled to the app's own design.
 *
 * The native control can't be themed beyond its box — the option list is drawn
 * by the OS, so it arrives as a white menu in the middle of a dark interface.
 *
 * The popup renders through a portal at the document root rather than inline:
 * several of these live inside modals and cards with `overflow: hidden` or
 * `auto`, which would clip an absolutely-positioned list. Fixed coordinates
 * recomputed on scroll and resize keep it pinned to the trigger.
 *
 * Keyboard behaviour follows the native control: Enter/Space/Arrows to open,
 * arrows to move, Enter to commit, Escape to cancel, Home/End to jump, and
 * type-ahead that matches on a prefix.
 */
export function Select({
  value,
  options,
  onChange,
  id,
  placeholder = 'Select…',
  disabled,
  mono,
  ariaLabel,
  className,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Exactly one of `top` / `bottom` is set: opening downward pins the top edge
  // to the trigger, opening upward pins the bottom edge to it.
  const [rect, setRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const typeahead = useRef({ query: '', at: 0 });

  const listboxId = useId();
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const position = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const box = trigger.getBoundingClientRect();
    const below = window.innerHeight - box.bottom - POPUP_MARGIN * 2;
    const above = box.top - POPUP_MARGIN * 2;

    // Flip upward when the space below can't hold a usable list.
    const openUp = below < Math.min(MAX_POPUP_HEIGHT, 180) && above > below;

    // maxHeight is a cap, not the height the list will actually take. Deriving
    // an upward `top` from it assumed a full-height list, so a four-row menu
    // under a 320px cap floated ~170px clear of the control it belongs to.
    // Pinning the bottom edge instead lets the list be whatever height it is.
    //
    // The cap is also clamped to the space actually available — a floor here
    // would push the list off the top of the screen in a short viewport.
    const maxHeight = Math.max(0, Math.min(MAX_POPUP_HEIGHT, openUp ? above : below));

    setRect({
      top: openUp ? undefined : box.bottom + POPUP_MARGIN,
      bottom: openUp ? window.innerHeight - box.top + POPUP_MARGIN : undefined,
      left: Math.max(
        POPUP_MARGIN,
        Math.min(box.left, window.innerWidth - box.width - POPUP_MARGIN),
      ),
      width: box.width,
      maxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    position();
  }, [open, position]);

  // Reposition rather than close on scroll: closing is jarring when the user
  // is scrolling the page behind a long settings form.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => position();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, position]);

  // Outside click. `mousedown` would fire before the option's click lands and
  // unmount the row underneath it, so this listens on `click`.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  // Keep the active row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    popupRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  function openList(startAt = selectedIndex >= 0 ? selectedIndex : 0) {
    if (disabled) return;
    setActiveIndex(startAt);
    setOpen(true);
  }

  function commit(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function step(from: number, direction: 1 | -1): number {
    const count = options.length;
    for (let i = 1; i <= count; i++) {
      const next = (from + direction * i + count * count) % count;
      if (!options[next]?.disabled) return next;
    }
    return from;
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;

    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(activeIndex);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => step(i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => step(i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(step(-1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(step(options.length, -1));
        break;
      case 'Tab':
        setOpen(false);
        break;
      default: {
        // Type-ahead: keystrokes within a second accumulate into a prefix.
        if (e.key.length !== 1) return;
        const now = Date.now();
        const t = typeahead.current;
        t.query = now - t.at > 1000 ? e.key : t.query + e.key;
        t.at = now;
        const match = options.findIndex(
          (o) => !o.disabled && o.label.toLowerCase().startsWith(t.query.toLowerCase()),
        );
        if (match >= 0) setActiveIndex(match);
      }
    }
  }

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={cx(s.trigger, open && s.triggerOpen, mono && s.mono, className)}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
        aria-label={ariaLabel}
      >
        <span className={cx(s.value, !selected && s.placeholder)}>
          {selected ? selected.label : placeholder}
        </span>
        <Chevron className={cx(s.chevron, open && s.chevronOpen)} />
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={popupRef}
              id={listboxId}
              role="listbox"
              className={cx(s.popup, mono && s.mono)}
              style={{
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                width: rect.width,
                maxHeight: rect.maxHeight,
              }}
            >
              {options.length === 0 ? (
                <div className={s.empty}>Nothing to choose from</div>
              ) : (
                options.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    id={`${listboxId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={option.value === value}
                    disabled={option.disabled}
                    className={cx(
                      s.option,
                      index === activeIndex && s.optionActive,
                      option.value === value && s.optionSelected,
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(index)}
                  >
                    <Tick className={cx(s.tick, option.value === value && s.tickOn)} />
                    <span className={s.optionLabel}>{option.label}</span>
                    {option.hint ? <span className={s.optionHint}>{option.hint}</span> : null}
                  </button>
                ))
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function Chevron({ className }: { className?: string }) {
  return <ChevronDown className={className} size={16} aria-hidden="true" />;
}
function Tick({ className }: { className?: string }) {
  return <Check className={className} size={16} aria-hidden="true" />;
}

/** Convenience for the common case of a plain list of strings. */
export function toOptions(values: readonly string[]): SelectOption[] {
  return values.map((v) => ({ value: v, label: v }));
}
